import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../utils/api';
import './Intelligence.css';

function Intelligence() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [queryLogs, setQueryLogs] = useState([]);
  const [modelPriorities, setModelPriorities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});
  const [activeTab, setActiveTab] = useState('settings');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilters, setLogFilters] = useState({
    route_type: '',
    hours: 24,
    skip: 0,
    limit: 50
  });

  const fetchSettings = useCallback(async () => {
    try {
      setError(null);
      const [settingsData, statsData] = await Promise.all([
        adminAPI.getIntelligenceSettings(),
        adminAPI.getIntelligenceStats()
      ]);
      setSettings(settingsData);
      setStats(statsData);
      setModelPriorities(settingsData.model_priorities || {});
    } catch (err) {
      console.error('Failed to fetch intelligence settings:', err);
      setError(err.response?.data?.detail || 'Failed to load intelligence settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQueryLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const logs = await adminAPI.getQueryLogs(logFilters);
      setQueryLogs(logs);
    } catch (err) {
      console.error('Failed to fetch query logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, [logFilters]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchQueryLogs();
    }
  }, [activeTab, fetchQueryLogs]);

  const handleToggle = async (key, currentValue) => {
    setUpdating(prev => ({ ...prev, [key]: true }));
    try {
      const newSettings = await adminAPI.updateIntelligenceSettings({ [key]: !currentValue });
      setSettings(newSettings);
      // Refresh stats after toggle
      const newStats = await adminAPI.getIntelligenceStats();
      setStats(newStats);
    } catch (err) {
      console.error(`Failed to toggle ${key}:`, err);
      setError(err.response?.data?.detail || `Failed to update ${key}`);
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handlePriorityChange = async (modelId, newPriority) => {
    const updatedPriorities = {
      ...modelPriorities,
      [modelId]: parseInt(newPriority) || 1
    };
    setModelPriorities(updatedPriorities);

    try {
      await adminAPI.updateIntelligenceSettings({ model_priorities: updatedPriorities });
    } catch (err) {
      console.error('Failed to update model priorities:', err);
      setError(err.response?.data?.detail || 'Failed to update model priorities');
    }
  };

  const handleAddModelPriority = () => {
    const modelId = prompt('Enter model ID (e.g., "claude/claude-3.5-sonnet"):');
    if (modelId && !modelPriorities[modelId]) {
      const priority = prompt('Enter priority (1 = highest):', '1');
      if (priority) {
        handlePriorityChange(modelId, priority);
      }
    }
  };

  const handleRemoveModelPriority = async (modelId) => {
    const updatedPriorities = { ...modelPriorities };
    delete updatedPriorities[modelId];
    setModelPriorities(updatedPriorities);

    try {
      await adminAPI.updateIntelligenceSettings({ model_priorities: updatedPriorities });
    } catch (err) {
      console.error('Failed to remove model priority:', err);
    }
  };

  const handleThresholdChange = async (key, value) => {
    // Validate value is between 0 and 1
    if (isNaN(value) || value < 0 || value > 1) return;

    // Update local state immediately for responsiveness
    setSettings(prev => ({ ...prev, [key]: value }));

    try {
      await adminAPI.updateIntelligenceSettings({ [key]: value });
    } catch (err) {
      console.error(`Failed to update ${key}:`, err);
      setError(err.response?.data?.detail || `Failed to update ${key}`);
      // Revert on error
      fetchSettings();
    }
  };

  if (loading) {
    return (
      <div className="intelligence">
        <div className="page-header">
          <h1 className="page-title">Intelligence</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <span>Loading intelligence settings...</span>
        </div>
      </div>
    );
  }

  const settingItems = [
    {
      key: 'ml_routing_enabled',
      label: 'ML Query Routing',
      description: 'Enable intelligent query routing to answer simple questions locally using ML instead of LLM calls.',
      icon: BrainIcon,
      isPrimary: true
    },
    {
      key: 'local_synthesis_enabled',
      label: 'Local Synthesis',
      description: 'Use local ML to synthesize council mode responses instead of an additional LLM call.',
      icon: MergeIcon,
      disabled: !settings?.ml_routing_enabled
    },
    {
      key: 'partial_answers_enabled',
      label: 'Partial Answers',
      description: 'Show partial ML answers with "Need in-depth analysis?" button for more comprehensive responses.',
      icon: PartialIcon,
      disabled: !settings?.ml_routing_enabled
    }
  ];

  return (
    <div className="intelligence">
      <div className="page-header">
        <h1 className="page-title">Intelligence</h1>
        <p className="page-subtitle">
          Configure intelligent query routing and local ML settings. Reduce LLM calls for simple, context-answerable questions.
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <ErrorIcon className="error-icon" />
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <SettingsIcon className="tab-icon" />
          Settings
        </button>
        <button
          className={`tab ${activeTab === 'priority' ? 'active' : ''}`}
          onClick={() => setActiveTab('priority')}
        >
          <PriorityIcon className="tab-icon" />
          Model Priority
        </button>
        <button
          className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          <LogsIcon className="tab-icon" />
          Query Logs
        </button>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <>
          {/* Status Overview */}
          {stats && (
            <div className="status-grid">
              <div className={`status-card ${settings?.ml_routing_enabled ? 'active' : ''}`}>
                <div className="status-indicator"></div>
                <div className="status-info">
                  <span className="status-label">ML Routing</span>
                  <span className="status-value">{settings?.ml_routing_enabled ? 'Active' : 'Disabled'}</span>
                </div>
              </div>
              <div className="status-card info">
                <div className="status-info">
                  <span className="status-label">Queries Today</span>
                  <span className="status-value">{stats.queries_today || 0}</span>
                </div>
              </div>
              <div className="status-card info">
                <div className="status-info">
                  <span className="status-label">ML Answered</span>
                  <span className="status-value">{stats.ml_answered_today || 0}</span>
                </div>
              </div>
              <div className="status-card info">
                <div className="status-info">
                  <span className="status-label">LLM Saved</span>
                  <span className="status-value">{stats.llm_calls_saved_percent || 0}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Toggle Controls */}
          <div className="settings-section">
            <h2>Feature Toggles</h2>
            <div className="settings-list">
              {settingItems.map(({ key, label, description, icon: Icon, isPrimary, disabled }) => (
                <div
                  key={key}
                  className={`setting-item ${isPrimary ? 'primary' : ''} ${disabled ? 'disabled' : ''}`}
                >
                  <div className="setting-icon">
                    <Icon className="icon" />
                  </div>
                  <div className="setting-content">
                    <div className="setting-header">
                      <span className="setting-label">{label}</span>
                      {disabled && <span className="disabled-badge">Requires ML Routing</span>}
                    </div>
                    <p className="setting-description">{description}</p>
                  </div>
                  <div className="setting-toggle">
                    <label className={`toggle-switch ${updating[key] ? 'updating' : ''}`}>
                      <input
                        type="checkbox"
                        checked={settings?.[key] || false}
                        onChange={() => handleToggle(key, settings?.[key])}
                        disabled={disabled || updating[key]}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Confidence Thresholds */}
          <div className="settings-section">
            <h2>Confidence Thresholds</h2>
            <p className="section-desc">
              Configure confidence thresholds for intelligent query routing and synthesis decisions.
              Queries below these thresholds are automatically routed to LLM.
            </p>
            <div className="thresholds-grid editable">
              <div className="threshold-item">
                <span className="threshold-label">Synthesis Confidence</span>
                <div className="threshold-input-wrapper">
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings?.synthesis_confidence_threshold ?? 0.5}
                    onChange={(e) => handleThresholdChange('synthesis_confidence_threshold', parseFloat(e.target.value))}
                    className="threshold-input"
                  />
                  <span className="threshold-hint">Local synthesis confidence</span>
                </div>
              </div>
              <div className="threshold-item">
                <span className="threshold-label">ML Routing Confidence</span>
                <div className="threshold-input-wrapper">
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings?.ml_routing_confidence_threshold ?? 0.7}
                    onChange={(e) => handleThresholdChange('ml_routing_confidence_threshold', parseFloat(e.target.value))}
                    className="threshold-input"
                  />
                  <span className="threshold-hint">Route to ML vs LLM</span>
                </div>
              </div>
              <div className="threshold-item">
                <span className="threshold-label">Meta-Synthesis Confidence</span>
                <div className="threshold-input-wrapper">
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings?.meta_synthesis_confidence_threshold ?? 0.6}
                    onChange={(e) => handleThresholdChange('meta_synthesis_confidence_threshold', parseFloat(e.target.value))}
                    className="threshold-input"
                  />
                  <span className="threshold-hint">Trigger meta-synthesis</span>
                </div>
              </div>
            </div>
            <p className="info-note">
              Confidence thresholds are global settings. Lower values allow more ML routing; higher values require more certainty before bypassing LLM calls.
            </p>
          </div>

          {/* Query Length Limit */}
          <div className="info-section">
            <h2>Query Routing Rules</h2>
            <div className="rules-grid">
              <div className="rule-item">
                <span className="rule-label">Max Query Length for ML</span>
                <span className="rule-value">150 characters</span>
              </div>
              <div className="rule-item">
                <span className="rule-label">Chat History Context</span>
                <span className="rule-value">8K characters</span>
              </div>
            </div>
            <p className="info-note">
              Queries longer than 150 characters are automatically routed to LLM.
              The last 8K characters of chat history are used for context.
            </p>
          </div>
        </>
      )}

      {/* Model Priority Tab */}
      {activeTab === 'priority' && (
        <div className="priority-section">
          <div className="section-header">
            <h2>Model Priority for Synthesis</h2>
            <button className="btn btn-primary btn-sm" onClick={handleAddModelPriority}>
              <PlusIcon /> Add Model
            </button>
          </div>
          <p className="section-desc">
            Set priority for models in council mode synthesis. Lower number = higher priority.
            When citations conflict, the higher priority model's citation wins.
          </p>

          {Object.keys(modelPriorities).length === 0 ? (
            <div className="empty-state">
              <PriorityIcon className="empty-icon" />
              <p>No model priorities configured</p>
              <p className="empty-hint">Add models to set their priority for citation conflict resolution</p>
            </div>
          ) : (
            <div className="priority-list">
              {Object.entries(modelPriorities)
                .sort(([, a], [, b]) => a - b)
                .map(([modelId, priority]) => (
                  <div key={modelId} className="priority-item">
                    <div className="priority-rank">#{priority}</div>
                    <div className="priority-model">
                      <span className="model-id">{modelId}</span>
                    </div>
                    <div className="priority-controls">
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={priority}
                        onChange={(e) => handlePriorityChange(modelId, e.target.value)}
                        className="priority-input"
                      />
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveModelPriority(modelId)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Query Logs Tab */}
      {activeTab === 'logs' && (
        <div className="logs-section">
          <div className="logs-header">
            <h2>Query Routing Logs</h2>
            <div className="logs-filters">
              <select
                value={logFilters.route_type}
                onChange={(e) => setLogFilters(prev => ({ ...prev, route_type: e.target.value, skip: 0 }))}
                className="filter-select"
              >
                <option value="">All Routes</option>
                <option value="local_ml">Local ML</option>
                <option value="llm">LLM</option>
                <option value="partial">Partial</option>
              </select>
              <select
                value={logFilters.hours}
                onChange={(e) => setLogFilters(prev => ({ ...prev, hours: parseInt(e.target.value), skip: 0 }))}
                className="filter-select"
              >
                <option value="24">Last 24 hours</option>
                <option value="48">Last 48 hours</option>
                <option value="168">Last 7 days</option>
              </select>
              <button className="btn btn-secondary btn-sm" onClick={fetchQueryLogs} disabled={logsLoading}>
                <RefreshIcon className={logsLoading ? 'spinning' : ''} /> Refresh
              </button>
            </div>
          </div>

          {logsLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <span>Loading query logs...</span>
            </div>
          ) : queryLogs.length === 0 ? (
            <div className="empty-state">
              <LogsIcon className="empty-icon" />
              <p>No query logs found</p>
              <p className="empty-hint">Query routing logs will appear here once ML routing is enabled and queries are processed</p>
            </div>
          ) : (
            <>
              <div className="logs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Query</th>
                      <th>Route</th>
                      <th>Classification</th>
                      <th>Confidence</th>
                      <th>Latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queryLogs.map((log) => (
                      <tr key={log.id}>
                        <td className="time-cell">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="query-cell" title={log.query}>
                          {log.query.length > 50 ? log.query.substring(0, 50) + '...' : log.query}
                        </td>
                        <td>
                          <span className={`route-badge ${log.route_type}`}>
                            {log.route_type}
                          </span>
                        </td>
                        <td>{log.classification || '-'}</td>
                        <td>
                          {log.confidence ? (
                            <span className={`confidence ${log.confidence >= 0.7 ? 'high' : 'low'}`}>
                              {(log.confidence * 100).toFixed(0)}%
                            </span>
                          ) : '-'}
                        </td>
                        <td>{log.latency_ms}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination">
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={logFilters.skip === 0}
                  onClick={() => setLogFilters(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                >
                  Previous
                </button>
                <span className="page-info">
                  Showing {logFilters.skip + 1} - {logFilters.skip + queryLogs.length}
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  disabled={queryLogs.length < logFilters.limit}
                  onClick={() => setLogFilters(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Icons
function BrainIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 4.5a2.5 2.5 0 0 0-4.96-.46 2.5 2.5 0 0 0-1.98 3 2.5 2.5 0 0 0-1.32 4.24 3 3 0 0 0 .34 5.58 2.5 2.5 0 0 0 2.96 3.08A2.5 2.5 0 0 0 12 19.5a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 12 4.5" />
      <path d="m15.7 10.4-.9.4" />
      <path d="m9.2 13.2-.9.4" />
      <path d="m13.6 15.7-.4-.9" />
      <path d="m10.8 9.2-.4-.9" />
      <path d="m15.7 13.5-.9-.4" />
      <path d="m9.2 10.9-.9-.4" />
      <path d="m10.5 15.7.4-.9" />
      <path d="m13.1 9.2.4-.9" />
    </svg>
  );
}

function MergeIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 3v6" />
      <path d="M18 3v6" />
      <path d="M6 9a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3" />
      <path d="M12 12v9" />
    </svg>
  );
}

function PartialIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a10 10 0 0 1 0 20" fill="currentColor" opacity="0.3" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function SettingsIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
    </svg>
  );
}

function PriorityIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h18M3 12h12M3 18h6" />
    </svg>
  );
}

function LogsIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function RefreshIcon({ className }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23,4 23,10 17,10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

function ErrorIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export default Intelligence;
